import React, {useEffect, useRef} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {LOCATIONIQ_KEY} from "./utils.tsx";

interface MiceStreetMapProps {
    latitude: number;
    longitude: number;
    name: string;
    accent: string;
}

// Marcador en SVG: el icono por defecto de Leaflet referencia PNGs por ruta relativa que Vite no resuelve
const pinIcon = (color: string) => L.divIcon({
    className: '',
    html: `<svg width="36" height="44" viewBox="0 0 24 28" aria-hidden="true">
        <path d="M12 27s9-8.5 9-15a9 9 0 1 0-18 0c0 6.5 9 15 9 15z" fill="#1D1B18"/>
        <circle cx="12" cy="12" r="3.5" fill="${color}"/>
    </svg>`,
    iconSize: [36, 44],
    iconAnchor: [18, 43],
    tooltipAnchor: [0, -40],
});

// Callejero interactivo (teselas de LocationIQ) para la pestaña Situación de MICE
export const MiceStreetMap: React.FC<MiceStreetMapProps> = ({latitude, longitude, name, accent}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current || !LOCATIONIQ_KEY) return;
        const map = L.map(containerRef.current, {
            center: [latitude, longitude],
            zoom: 15,
            // La rueda hace scroll de la página; el zoom va con los botones, doble clic o pellizco
            scrollWheelZoom: false,
            // En táctil, arrastrar con un dedo debe desplazar la ficha, no el mapa
            dragging: !L.Browser.mobile,
        });
        L.tileLayer(`https://tiles.locationiq.com/v3/streets/r/{z}/{x}/{y}.png?key=${LOCATIONIQ_KEY}`, {
            maxZoom: 18,
            attribution: '<a href="https://locationiq.com" target="_blank" rel="noopener">© LocationIQ</a> '
                + '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap</a>',
        }).addTo(map);
        L.marker([latitude, longitude], {icon: pinIcon(accent), title: name, alt: name})
            .bindTooltip(name, {permanent: true, direction: 'top', className: 'font-semibold'})
            .addTo(map);

        // El contenedor puede montarse con tamaño 0 (transición de pestañas): se recalcula al cambiar
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(containerRef.current);
        return () => {
            observer.disconnect();
            map.remove();
        };
    }, [latitude, longitude, name, accent]);

    return (
        <div className="relative w-full h-full">
            {/* swiper-no-swiping: arrastrar el mapa no debe pasar de página del catálogo */}
            <div ref={containerRef} className="swiper-no-swiping w-full h-full z-0"
                 role="region" aria-label={`Mapa de calles con la ubicación de ${name}`}/>
            <a href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
               target="_blank" rel="noopener noreferrer"
               // color en línea: la regla global a { color } de index.css gana a las utilidades de Tailwind
               style={{color: '#1D1B18'}}
               className="absolute left-3 bottom-3 z-[500] flex items-center gap-2 min-h-[40px] px-3.5 rounded-xl bg-white text-[13px] font-semibold no-underline shadow-md hover:bg-[#F7F6F2] focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D1B18]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <path d="M15 3h6v6"/>
                    <path d="M10 14L21 3"/>
                </svg>
                Cómo llegar
            </a>
        </div>
    );
};
